import z from "zod";

export const MetadataSchema = z.object({
  otp: z.string().length(6),
});
export type MetadataType = z.infer<typeof MetadataSchema>;

export const PersonalDetailsSchema = z
  .object({
    parq_name: z.string().min(2),
    parq_surname: z.string().min(2),
    parq_number: z
      .string()
      .length(13, { message: "Please enter a valid number." }),
    parq_email: z.string().email(),
    parq_dob: z.date(),
    parq_emergency_name: z.string().min(2),
    parq_emergency_relation: z.string().min(2),
    parq_emergency_number: z
      .string()
      .length(13, { message: "Please enter a valid number." }),
    parq_emergency_email: z.email().optional(),
  })
  .refine((data) => data.parq_number !== data.parq_emergency_number, {
    path: ["parq_emergency_number"],
    message: "Emergency contact number must be different from your number.",
  })
  .refine((data) => data.parq_email !== data.parq_emergency_email, {
    path: ["parq_emergency_email"],
    message: "Emergency contact email must be different from your email.",
  });
export type PersonalDetailsType = z.infer<typeof PersonalDetailsSchema>;

export const QuestionSet = {
  question_1:
    "Has your doctor ever said you have a bone or joint problem, such as arthritis, that has been aggravated by exercise or might be made worse by exercise?",
  question_2: "Do you have high blood pressure?",
  question_3: "Do you have low blood pressure?",
  question_4: "Do you have Diabetes or any other Metabolic disease?",
  question_5:
    "Has your doctor ever said you have raised cholesterol (above 6.2mmol/L)?",
  question_6: "Has your doctor ever said you have a heart condition?",
  question_7: "Have you ever felt pain in your chest when exercising?",
  question_8:
    "Is your doctor currently prescribing drugs and medication that may effect your ability to safely exercise?",
  question_9:
    "Have you ever suffered from unusual shortness of breath at rest or with mild exertion (walking)?",
  question_10: "Is there a history of Coronary Heart Disease in your family?",
  question_11:
    "Do you often feel faint, have spells of dizziness, lost consciousness, suffer from unusual slurred speech, feel nauseas?",
  question_12:
    "Do you currently drink more than the average amount of alcohol per week (21 units for men and 14 units for women)?",
  question_13: "Do you currently smoke?",
  question_14: "Are you pregnant?",
  question_15:
    "Do you have any other reason not mentioned above that may affect your ability to exercise?",
  question_15b: "If yes, please give details.",
};

export const Question1Schema = z.object({
  question_1: z.enum(["yes", "no"]),
  question_2: z.enum(["yes", "no"]),
  question_3: z.enum(["yes", "no"]),
  question_4: z.enum(["yes", "no"]),
  question_5: z.enum(["yes", "no"]),
});
export type Question1Type = z.infer<typeof Question1Schema>;

export const Question2Schema = z.object({
  question_6: z.enum(["yes", "no"]),
  question_7: z.enum(["yes", "no"]),
  question_8: z.enum(["yes", "no"]),
  question_9: z.enum(["yes", "no"]),
  question_10: z.enum(["yes", "no"]),
});
export type Question2Type = z.infer<typeof Question2Schema>;

export const Question3Schema = z
  .object({
    question_11: z.enum(["yes", "no"]),
    question_12: z.enum(["yes", "no"]),
    question_13: z.enum(["yes", "no"]),
    question_14: z.enum(["yes", "no"]),
    question_15: z.enum(["yes", "no"]),
    question_15b: z.string().optional(),
  })
  .refine(
    (data) => {
      if (
        data.question_15 === "yes" &&
        (!data.question_15b || data.question_15b.trim() === "")
      )
        return false;
      return true;
    },
    {
      path: ["question_15b"],
      message: "Please provide more information for question 15.",
    }
  );
export type Question3Type = z.infer<typeof Question3Schema>;

export const SignSchema = z.object({
  parq_signature: z
    .string({
      message: "Signature is required.",
    })
    .transform((val) => {
      val = val.replace(/^data:image\/[^;]+;base64,/, "");
      return val;
    }),
  parq_name_block: z.string(),
  parq_signature_date: z.date(),
});
export type SignType = z.infer<typeof SignSchema>;

export const ParqFormSchema = MetadataSchema.and(PersonalDetailsSchema)
  .and(Question1Schema)
  .and(Question2Schema)
  .and(Question3Schema)
  .and(SignSchema);
export const ParqFormSchemaPart = MetadataSchema.or(PersonalDetailsSchema)
  .or(Question1Schema)
  .or(Question2Schema)
  .or(Question3Schema)
  .or(SignSchema);

export type ParqFormType = z.infer<typeof ParqFormSchema>;
export type ParqFormTypePart = z.infer<typeof ParqFormSchemaPart>;

export interface ParqProps {
  parqData: ParqFormType;
}
